import type { Message } from '../api/types'

/**
 * The order of a chat (#130, 1.115.0) -- one place for the browser, like
 * `ChatOrder` for the server.
 *
 * **The id is the moment, `sort_key` is the place.** A post inserted on
 * Friday about Tuesday has a high id and a key between Tuesday's posts. Every
 * question "which of the two stands further down" goes through here; a
 * question "which was written later" (read mark, unread edge, the filter's
 * "since") stays with the id.
 *
 * A row without a key (a page from a store older than 1.115.0) sorts by id
 * alone, so a stale page never throws -- and it is dropped anyway when the
 * store's version changes.
 */
type Placed = Pick<Message, 'id' | 'sort_key'>

/** Negative when `a` stands above `b` in the chat (older place), positive below. */
export function compareMessages(a: Placed, b: Placed): number {
  const ka = a.sort_key ?? 0
  const kb = b.sort_key ?? 0
  if (ka !== kb) return ka - kb
  return a.id - b.id
}

/** A list in the cache's order: the newest place first. */
export function newestFirst<T extends Placed>(list: T[]): T[] {
  return [...list].sort((a, b) => compareMessages(b, a))
}
